import React, { useEffect, useState } from "react";
import { User } from "lucide-react";
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../../services/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  ROLE_UI_OPTIONS,
  UI_LABEL_TO_ROLE,
  type OnboardingRole,
} from "./onboardingFlowConfig.ts";

export function AccountSetupStep({
  onValidChange,
  experimentVariant,
}: {
  onValidChange: (isValid: boolean) => void;
  experimentVariant?: string;
}) {
  const [userType, setUserType] = useState("");
  const [userData, setUserData] = useState<{
    id: string;
    attribution?: { landing_role_intent?: string };
  } | null>(null);
  const isValid = Boolean(userType);

  const fetchUserData = async () => {
    try {
      const q = query(
        collection(db, "users"),
        where("uid", "==", auth.currentUser!.uid)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const data = { id: userDoc.id, ...userDoc.data() } as {
          id: string;
          userType?: string;
          onboardingRole?: OnboardingRole;
          attribution?: { landing_role_intent?: string };
        };
        setUserData(data);
        if (data.userType) {
          setUserType(data.userType);
        } else if (data.attribution?.landing_role_intent) {
          const intent = data.attribution.landing_role_intent;
          const match = ROLE_UI_OPTIONS.find(
            (label) => UI_LABEL_TO_ROLE[label] === intent
          );
          if (match) setUserType(match);
        }
      } else {
        console.error("No user data found for the current UID");
      }
    } catch (error) {
      console.error("Error fetching user data: ", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserData();
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const updateUserDoc = async () => {
      if (userData?.id && isValid) {
        const userRef = doc(db, "users", userData.id);
        const onboardingRole = UI_LABEL_TO_ROLE[userType] ?? "band_director";

        try {
          await updateDoc(userRef, {
            userType,
            onboardingRole,
            ...(experimentVariant
              ? { onboardingExperimentVariant: experimentVariant }
              : {}),
          });
        } catch (error) {
          console.error("Error updating document: ", error);
        }
      }
    };

    updateUserDoc();
  }, [isValid, userData?.id, userType, experimentVariant]);

  React.useEffect(() => {
    onValidChange(isValid);
  }, [isValid, onValidChange]);

  return (
    <div className="max-w-md mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Tell us about yourself
      </h2>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            I am a...
          </label>
          <div className="grid grid-cols-2 gap-4">
            {ROLE_UI_OPTIONS.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setUserType(type)}
                className={`p-3 border rounded-lg flex items-center justify-center transition-colors
                  ${
                    userType === type
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-300 hover:bg-gray-50"
                  }`}
              >
                <User className="w-4 h-4 mr-2" />
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
